import React, { Component } from "react";
import PropTypes from "prop-types";
import { Select } from "@terraincognita/ui-core";
import { RaisedButton } from "material-ui";
import { PublishStatusBanner } from "./PublishStatusBanner";

const PUBLISH_POLL_INTERVAL = 5000;
const COMPLETE_BANNER_DURATION = 15 * 1000;

export class PublishManager extends Component {
  constructor(props) {
    super(props);
    this.state = {
      publishInitiated: false,
      publishCompletedAt: null,
      completeBannerDismissed: false,
      errorDismissed: false
    };
    this._publishStatusInterval = null;
    this._completeBannerTimeout = null;
    this.handleProjectChange = this.handleProjectChange.bind(this);
    this.handlePublishClick = this.handlePublishClick.bind(this);
    this.handleCloseBanner = this.handleCloseBanner.bind(this);
    this.handleDismissError = this.handleDismissError.bind(this);
  }

  componentDidMount() {
    if (this.isPublishingInProgress()) {
      this.startPublishPolling();
    } else {
      this.checkForRecentCompletion();
    }
  }

  componentDidUpdate(prevProps) {
    const wasInProgress = this.isPublishingInProgressFromData(prevProps.kioskData);
    const isInProgress = this.isPublishingInProgress();

    if (this.state.publishInitiated && isInProgress && !this.isLastPublishError()) {
      this.setState({ publishInitiated: false });
    }

    if (wasInProgress && !isInProgress && !this.isLastPublishCancelled()) {
      this.showCompleteBanner();
    }

    if (!prevProps.kioskData && this.props.kioskData && !isInProgress && !this.state.publishCompletedAt) {
      this.checkForRecentCompletion();
    }

    if (!this._publishStatusInterval && isInProgress) {
      this.startPublishPolling();
    } else if (this._publishStatusInterval && !isInProgress && wasInProgress) {
      this.stopPublishPolling();
    }
  }

  componentWillUnmount() {
    this.stopPublishPolling();
    if (this._completeBannerTimeout) {
      clearTimeout(this._completeBannerTimeout);
      this._completeBannerTimeout = null;
    }
  }

  isPublishingInProgressFromData(kioskData) {
    return !!(
      kioskData &&
      kioskData.publishStatus &&
      kioskData.publishStatus !== "complete"
    );
  }

  isPublishingInProgress() {
    return this.isPublishingInProgressFromData(this.props.kioskData);
  }

  isLastPublishCancelled() {
    const { kioskData } = this.props;
    if (!kioskData || !kioskData.publishHistory || kioskData.publishHistory.length === 0) {
      return false;
    }
    const lastEntry = kioskData.publishHistory[kioskData.publishHistory.length - 1];
    return !!(lastEntry && lastEntry.metadata && lastEntry.metadata.cancelledAt);
  }

  isLastPublishError() {
    const { kioskData } = this.props;
    if (!kioskData || !kioskData.publishHistory || kioskData.publishHistory.length === 0) {
      return false;
    }
    const lastEntry = kioskData.publishHistory[kioskData.publishHistory.length - 1];
    return !!(lastEntry && lastEntry.metadata && lastEntry.metadata.error);
  }

  handleDismissError() {
    this.setState({ errorDismissed: true });
  }

  startPublishPolling() {
    this.stopPublishPolling();
    this.props.requestKiosk();
    this._publishStatusInterval = setInterval(() => {
      this.props.requestKiosk();
    }, PUBLISH_POLL_INTERVAL);
  }

  stopPublishPolling() {
    if (this._publishStatusInterval) {
      clearInterval(this._publishStatusInterval);
      this._publishStatusInterval = null;
    }
  }

  showCompleteBanner() {
    if (this._completeBannerTimeout) {
      clearTimeout(this._completeBannerTimeout);
    }
    this.setState({ publishCompletedAt: Date.now(), completeBannerDismissed: false });
    this._completeBannerTimeout = setTimeout(() => {
      this.setState({ publishCompletedAt: null });
      this._completeBannerTimeout = null;
    }, COMPLETE_BANNER_DURATION);
  }

  handleCloseBanner() {
    if (this._completeBannerTimeout) {
      clearTimeout(this._completeBannerTimeout);
      this._completeBannerTimeout = null;
    }
    this.setState({ publishCompletedAt: null, completeBannerDismissed: true });
  }

  checkForRecentCompletion() {
    const { kioskData } = this.props;
    if (!kioskData || !kioskData.publishHistory || kioskData.publishHistory.length === 0) {
      return;
    }
    if (this.isPublishingInProgress() || this.isLastPublishCancelled()) {
      return;
    }
    const history = kioskData.publishHistory;
    const lastCompleteEntry = [...history]
      .reverse()
      .find(entry => entry.status === "complete");
    if (!lastCompleteEntry) {
      return;
    }
    const completedAt = new Date(lastCompleteEntry.timestamp).getTime();
    const elapsed = Date.now() - completedAt;
    if (elapsed < COMPLETE_BANNER_DURATION) {
      const remaining = COMPLETE_BANNER_DURATION - elapsed;
      this.setState({ publishCompletedAt: completedAt, completeBannerDismissed: false });
      this._completeBannerTimeout = setTimeout(() => {
        this.setState({ publishCompletedAt: null });
        this._completeBannerTimeout = null;
      }, remaining);
    }
  }

  stripHtmlTags(html) {
    if (!html) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  }

  handleProjectChange(name, value) {
    if (!value || value === "") {
      return;
    }
    /*
    Update only local state -- do NOT persist the project change to
    the kiosk record yet. The dropdown is meant to (1) show what is
    currently published on the kiosk and (2) let the user pick what
    to publish next; the kiosk's stored projectID should only change
    when the user actually publishes. The selected project is sent
    along in the publish request body and persisted server-side as
    part of the publish flow (matching the revert flow's behavior).
    */
    this.props.handleSettingsInputChange("projectID", value);
  }

  handlePublishClick() {
    if (this._completeBannerTimeout) {
      clearTimeout(this._completeBannerTimeout);
      this._completeBannerTimeout = null;
    }
    this.setState({ publishInitiated: true, publishCompletedAt: null, completeBannerDismissed: false, errorDismissed: false });
    this.props.publishKioskProject();
    this.startPublishPolling();
  }

  render() {
    const { projects, kioskData } = this.props;
    const isOnline = kioskData?.isOnline;
    const publishingInProgress = this.isPublishingInProgress();
    const isLastPublishCancelled = this.isLastPublishCancelled();
    const isLastPublishError = this.isLastPublishError();

    if (this.state.publishInitiated || (publishingInProgress && !isLastPublishCancelled && !isLastPublishError)) {
      const viewDetailsHandler = this.state.publishInitiated
        ? () => this.props.handleViewDetailsClick(true)
        : this.props.handleViewDetailsClick;
      return (
        <PublishStatusBanner
          status="publishing"
          onViewDetails={viewDetailsHandler}
          onCancel={this.props.handlePublishCancelClick}
        />
      );
    }

    if (isLastPublishError && !this.state.errorDismissed) {
      return (
        <PublishStatusBanner
          status="error"
          onViewDetails={this.props.handleViewDetailsClick}
          onTryAgain={this.handlePublishClick}
          onDismiss={this.handleDismissError}
        />
      );
    }

    if (this.state.publishCompletedAt && !this.state.completeBannerDismissed) {
      return (
        <PublishStatusBanner
          status="complete"
          onViewDetails={this.props.handleViewDetailsClick}
          onClose={this.handleCloseBanner}
        />
      );
    }

    let projectList = [];
    if (projects) {
      if (Array.isArray(projects)) {
        projectList = projects;
      } else {
        projectList = Object.values(projects);
      }
    }

    const projectOptions = projectList
      .map(project => {
        const title = project.title?.en || project.title || "Untitled Project";
        return {
          value: project._id,
          label: this.stripHtmlTags(title)
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

    if (!kioskData?.projectID) {
      projectOptions.unshift({ value: "", label: "Select a project" });
    }

    const currentProjectID = kioskData?.projectID;
    const hasProjectSelected = !!currentProjectID;
    const isPublishDisabled = !hasProjectSelected || !isOnline || (publishingInProgress && !isLastPublishCancelled && !this.isLastPublishError());

    return (
      <div className="publish-manager" style={{ padding: "65px 75px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div className="project-select" style={{ minWidth: 200 }}>
          <Select
            value={currentProjectID || ""}
            options={projectOptions}
            onChange={this.handleProjectChange}
            name="projectID"
            hintText="Select a project"
            style={{ width: 200 }}
          />
        </div>
        <RaisedButton
          onClick={this.handlePublishClick}
          label="Publish"
          primary
          disabled={isPublishDisabled}
          className="publish-button"
        />
        {!hasProjectSelected && (
          <div className="publish-hint-no-project" style={{ color: "#666", fontSize: 14 }}>
            Select a project to publish to the kiosk.
          </div>
        )}
        {hasProjectSelected && !isOnline && (
          <div className="publish-hint-offline" style={{ color: "#666", fontSize: 14 }}>
            The kiosk computer needs to be online in order to publish.
          </div>
        )}
      </div>
    );
  }
}

PublishManager.propTypes = {
  projects: PropTypes.object,
  kioskData: PropTypes.object,
  handleSettingsInputChange: PropTypes.func.isRequired,
  publishKioskProject: PropTypes.func.isRequired,
  requestKiosk: PropTypes.func.isRequired,
  handlePublishCancelClick: PropTypes.func.isRequired,
  handleViewDetailsClick: PropTypes.func.isRequired
};
